import React, { useEffect, useState } from "react";
import { Radio, Space, Row, Col, Button, Select, Table, Modal, Checkbox, Tag, Tooltip } from "antd";
import { Trash2, PencilIcon } from "lucide-react";
import { ShowToast, Severty } from "../helper/toast";
import apiPath from "../constants/apiPath";
import useRequest from "../hooks/useRequest";
import VehicleManufacturerSelect from "./select/VehicleManufacturerSelect";

const VehicleMappingSection = ({ segmentId, value = {}, onChange }) => {
    const { request } = useRequest();

    const vehicleOption = value.vehicleOption ?? null;
    const selectedManufacturers = value.selectedManufacturers ?? [];
    const vehicleModelMappings = value.vehicleModelMappings ?? [];

    const emit = (patch) =>
        onChange?.({ vehicleOption, selectedManufacturers, vehicleModelMappings, ...patch });

    // Add-form state
    const [selectedManufacturer, setSelectedManufacturer] = useState(null);
    const [selectedVehicles, setSelectedVehicles] = useState([]);

    const [manufacturers, setManufacturers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [loadingManufacturers, setLoadingManufacturers] = useState(false);
    const [loadingVehicles, setLoadingVehicles] = useState(false);

    // Model modal state
    const [modelModalVisible, setModelModalVisible] = useState(false);
    const [modalRow, setModalRow] = useState(null);
    const [modalModels, setModalModels] = useState([]);
    const [modalSelectedModels, setModalSelectedModels] = useState([]);
    const [loadingModalModels, setLoadingModalModels] = useState(false);

    // vehicle_id → [{_id, en_name}]
    const [modelsCache, setModelsCache] = useState({});
    // model_id → en_name
    const [modelNamesCache, setModelNamesCache] = useState({});

    useEffect(() => {
        setSelectedManufacturer(null);
        setSelectedVehicles([]);
        setVehicles([]);
    }, [segmentId]);

    useEffect(() => {
        if (vehicleOption !== "specific") return;
        setLoadingManufacturers(true);
        request({
            url: `${apiPath.commonManufacturer}?segment_id=${segmentId || ""}`,
            method: "GET",
            onSuccess: (res) => {
                setManufacturers(res.data?.data || []);
                setLoadingManufacturers(false);
            },
            onError: () => setLoadingManufacturers(false),
        });
    }, [segmentId, vehicleOption]);

    useEffect(() => {
        if (!selectedManufacturer) {
            setVehicles([]);
            setSelectedVehicles([]);
            return;
        }
        setLoadingVehicles(true);
        request({
            url: `${apiPath.commonVehicle}?manufacturer_id=${selectedManufacturer}&segment_id=${segmentId || ""}`,
            method: "GET",
            onSuccess: (res) => {
                setVehicles(res.data?.data || []);
                setLoadingVehicles(false);
            },
            onError: () => setLoadingVehicles(false),
        });
    }, [selectedManufacturer]);

    const handleOptionChange = (newOption) => {
        setSelectedManufacturer(null);
        setSelectedVehicles([]);
        setVehicles([]);
        emit({ vehicleOption: newOption, selectedManufacturers: [], vehicleModelMappings: [] });
    };

    const handleAddMapping = () => {
        if (!selectedManufacturer) {
            ShowToast("Please select a manufacturer", Severty.ERROR);
            return;
        }
        if (selectedVehicles.length === 0) {
            ShowToast("Please select at least one vehicle", Severty.ERROR);
            return;
        }

        const manufacturerName = manufacturers.find((m) => m._id === selectedManufacturer)?.en_name;
        const existingVehicleIds = new Set(vehicleModelMappings.map((m) => m.vehicle_id));
        const newVehicleIds = selectedVehicles.filter((vid) => !existingVehicleIds.has(vid));

        if (newVehicleIds.length === 0) {
            ShowToast("All selected vehicles are already added", Severty.WARNING);
            return;
        }

        const newMappings = newVehicleIds.map((vehicleId) => ({
            id: `${Date.now()}-${Math.random()}`,
            manufacturer_id: selectedManufacturer,
            manufacturer_name: manufacturerName,
            vehicle_id: vehicleId,
            vehicle_name: vehicles.find((v) => v._id === vehicleId)?.en_name,
            model_selection: "all",
            specific_models: [],
        }));

        emit({ vehicleModelMappings: [...vehicleModelMappings, ...newMappings] });

        setSelectedManufacturer(null);
        setSelectedVehicles([]);
        setVehicles([]);

        const skipped = selectedVehicles.length - newVehicleIds.length;
        if (skipped > 0) {
            ShowToast(`${newVehicleIds.length} vehicle(s) added, ${skipped} duplicate(s) skipped`, Severty.SUCCESS);
        } else {
            ShowToast(`${newVehicleIds.length} vehicle(s) added successfully`, Severty.SUCCESS);
        }
    };

    const handleRemoveMapping = (mappingKey) => {
        emit({
            vehicleModelMappings: vehicleModelMappings.filter(
                (m) => (m.id ?? m._id?.toString()) !== mappingKey
            ),
        });
    };

    const openModelModal = (row) => {
        setModalRow(row);
        setModalSelectedModels(row.specific_models || []);
        setModelModalVisible(true);

        const vehicleId = row.vehicle_id?.toString?.() ?? row.vehicle_id;

        if (modelsCache[vehicleId]) {
            setModalModels(modelsCache[vehicleId]);
            return;
        }

        setLoadingModalModels(true);
        request({
            url: `${apiPath.commonVehicleModel}?vehicle_id=${vehicleId}&segment_id=${segmentId || ""}`,
            method: "GET",
            onSuccess: (res) => {
                const list = res.data?.data || [];
                setModalModels(list);
                setModelsCache((prev) => ({ ...prev, [vehicleId]: list }));
                const namesMap = {};
                list.forEach((m) => { namesMap[m._id] = m.en_name; });
                setModelNamesCache((prev) => ({ ...prev, ...namesMap }));
                setLoadingModalModels(false);
            },
            onError: () => setLoadingModalModels(false),
        });
    };

    const handleModalConfirm = () => {
        if (!modalRow) return;
        const rowKey = modalRow.id ?? modalRow._id?.toString();
        const updatedMappings = vehicleModelMappings.map((m) => {
            const key = m.id ?? m._id?.toString();
            if (key !== rowKey) return m;
            return {
                ...m,
                model_selection: modalSelectedModels.length === 0 ? "all" : "specific",
                specific_models: modalSelectedModels,
            };
        });
        emit({ vehicleModelMappings: updatedMappings });
        setModelModalVisible(false);
        setModalRow(null);
        setModalSelectedModels([]);
    };

    const handleModalCancel = () => {
        setModelModalVisible(false);
        setModalRow(null);
        setModalSelectedModels([]);
    };

    const getModelDisplayText = (row) => {
        if (row.model_selection === "all" || !row.specific_models?.length) return null;
        const names = row.specific_models
            .map((id) => modelNamesCache[id?.toString?.() ?? id])
            .filter(Boolean);
        if (names.length === row.specific_models.length) return names.join(", ");
        return `${row.specific_models.length} model(s) selected`;
    };

    const tableColumns = [
        {
            title: "Manufacturer",
            key: "manufacturer",
            render: (_, r) => (
                <Tag color="blue" style={{ margin: 0 }}>
                    {r.manufacturer_name || r.manufacturer_id}
                </Tag>
            ),
        },
        {
            title: "Vehicle",
            key: "vehicle",
            render: (_, r) => (
                <Tag color="purple" style={{ margin: 0 }}>
                    {r.vehicle_name || r.vehicle_id}
                </Tag>
            ),
        },
        {
            title: "Models",
            key: "models",
            render: (_, r) => {
                const displayText = getModelDisplayText(r);
                const btn = (
                    <Button
                        size="small"
                        type="dashed"
                        onClick={() => openModelModal(r)}
                        icon={<PencilIcon size={9} />}
                        style={{
                            margin: 0,
                            padding: "0 5px",
                            fontSize: 11,
                            height: 20,
                            lineHeight: "18px",
                            borderColor: displayText ? "#fa8c16" : "#52c41a",
                            color: displayText ? "#fa8c16" : "#52c41a",
                        }}
                    >
                        {displayText
                            ? displayText.length > 30 ? displayText.slice(0, 30) + "…" : displayText
                            : "All Models"}
                    </Button>
                );
                return displayText && displayText.length > 30
                    ? <Tooltip title={displayText}>{btn}</Tooltip>
                    : btn;
            },
        },
        {
            title: "",
            key: "action",
            width: 40,
            render: (_, r) => (
                <Tooltip title="Remove">
                    <Button
                        type="text"
                        danger
                        size="small"
                        icon={<Trash2 size={14} />}
                        onClick={() => handleRemoveMapping(r.id ?? r._id?.toString())}
                    />
                </Tooltip>
            ),
        },
    ];

    return (
        <>
            <Radio.Group value={vehicleOption} onChange={(e) => handleOptionChange(e.target.value)}>
                <Space direction="vertical" className="w-full" size="large">
                    <Row gutter={[24, 0]} className="space-y-4">

                        {/* Option 1: All Vehicles */}
                        <Col span={24}>
                            <div className={`p-4 rounded-lg border shadow-sm cursor-pointer transition-colors ${vehicleOption === "all" ? "border-blue-500 bg-blue-50" : "bg-white hover:border-gray-300"}`}>
                                <Radio value="all">
                                    <span className="font-medium">Option 1: Available for All Vehicles</span>
                                    <p className="text-gray-500 text-sm mt-1 ml-6">
                                        Product will be available for all vehicles and their models
                                    </p>
                                </Radio>
                            </div>
                        </Col>

                        {/* Option 2: By Manufacturers */}
                        <Col span={24}>
                            <div className={`p-4 rounded-lg border shadow-sm cursor-pointer transition-colors ${vehicleOption === "manufacturers" ? "border-blue-500 bg-blue-50" : "bg-white hover:border-gray-300"}`}>
                                <Radio value="manufacturers">
                                    <span className="font-medium">Option 2: All Vehicles of Selected Manufacturers</span>
                                    <p className="text-gray-500 text-sm mt-1 ml-6">
                                        Select one or more manufacturers — product available for all their vehicles and models
                                    </p>
                                </Radio>

                                {vehicleOption === "manufacturers" && (
                                    <div className="ml-6 mt-3 p-4 bg-white border rounded">
                                        <h5 className="font-semibold mb-2">
                                            Select Vehicle Manufacturers <span className="text-red-500">*</span>
                                        </h5>
                                        <VehicleManufacturerSelect
                                            segmentId={segmentId}
                                            value={selectedManufacturers}
                                            onChange={(val) => emit({ selectedManufacturers: val })}
                                            multiple={true}
                                        />
                                    </div>
                                )}
                            </div>
                        </Col>

                        {/* Option 3: Specific Vehicles & Models */}
                        <Col span={24}>
                            <div className={`p-4 rounded-lg border shadow-sm cursor-pointer transition-colors ${vehicleOption === "specific" ? "border-blue-500 bg-blue-50" : "bg-white hover:border-gray-300"}`}>
                                <Radio value="specific">
                                    <span className="font-medium">Option 3: Specific Vehicles & Models</span>
                                    <p className="text-gray-500 text-sm mt-1 ml-6">
                                        Select vehicles — click the <Button size="small" type="dashed" icon={<PencilIcon size={9} />} style={{ fontSize: 11, height: 20, padding: "0 5px", lineHeight: "18px", borderColor: "#52c41a", color: "#52c41a", pointerEvents: "none" }}>All Models</Button> button in the table to narrow to specific models
                                    </p>
                                </Radio>

                                {vehicleOption === "specific" && (
                                    <div className="ml-6 mt-3 rounded space-y-4">

                                        {/* Add Mapping Form */}
                                        <div className="rounded p-4 bg-white border shadow-sm">
                                            <Row gutter={[16, 16]}>
                                                <Col xs={24} sm={20}>
                                                    <h5 className="font-semibold mb-2">
                                                        Manufacturer <span className="text-red-500">*</span>
                                                    </h5>
                                                    <Select
                                                        showSearch
                                                        allowClear
                                                        placeholder="Select manufacturer"
                                                        value={selectedManufacturer}
                                                        onChange={(val) => {
                                                            setSelectedManufacturer(val ?? null);
                                                            setSelectedVehicles([]);
                                                            setVehicles([]);
                                                        }}
                                                        loading={loadingManufacturers}
                                                        style={{ width: "100%", height: 40 }}
                                                        optionFilterProp="label"
                                                        options={manufacturers.map((m) => ({ label: m.en_name, value: m._id }))}
                                                    />
                                                </Col>

                                                <Col xs={24} sm={4}>
                                                    <h5 className="font-semibold mb-2 invisible">_</h5>
                                                    <Button
                                                        type="primary"
                                                        onClick={handleAddMapping}
                                                        disabled={!selectedManufacturer || selectedVehicles.length === 0}
                                                        style={{ width: "100%", height: 40 }}
                                                    >
                                                        Add
                                                    </Button>
                                                </Col>

                                                <Col span={24}>
                                                    <h5 className={`font-semibold mb-2 ${!selectedManufacturer ? "text-gray-400" : ""}`}>
                                                        Vehicles <span className="text-red-500">*</span>
                                                    </h5>
                                                    <Select
                                                        mode="multiple"
                                                        showSearch
                                                        allowClear
                                                        placeholder={selectedManufacturer ? "Select one or more vehicles…" : "Select manufacturer first"}
                                                        value={selectedVehicles}
                                                        onChange={setSelectedVehicles}
                                                        loading={loadingVehicles}
                                                        disabled={!selectedManufacturer}
                                                        style={{ width: "100%" }}
                                                        optionFilterProp="label"
                                                        options={vehicles.map((v) => ({ label: v.en_name, value: v._id }))}
                                                    />
                                                </Col>
                                            </Row>
                                        </div>

                                        {/* Mapping rows table */}
                                        {vehicleModelMappings.length === 0 ? (
                                            <div className="border border-dashed rounded-lg p-6 text-center text-gray-400 bg-gray-50 text-sm">
                                                No vehicles added yet. Select a manufacturer and vehicles above, then click <strong>Add</strong>.
                                            </div>
                                        ) : (
                                            <div className="border bg-white rounded-lg overflow-hidden">
                                                <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b">
                                                    <span className="font-semibold text-gray-700 text-sm">
                                                        Added Vehicles
                                                    </span>
                                                    <Tag color="blue">{vehicleModelMappings.length} row{vehicleModelMappings.length !== 1 ? "s" : ""}</Tag>
                                                </div>
                                                <Table
                                                    dataSource={vehicleModelMappings}
                                                    pagination={false}
                                                    size="small"
                                                    rowKey={(r) => r.id ?? r._id?.toString()}
                                                    columns={tableColumns}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                </Space>
            </Radio.Group>

            {/* Model Selection Modal */}
            <Modal
                title={
                    <div>
                        <div>Select Models - <span className="text-purple-600">{modalRow?.vehicle_name || ""}</span></div>
                    </div>
                }
                open={modelModalVisible}
                onOk={handleModalConfirm}
                onCancel={handleModalCancel}
                okText="Apply"
                destroyOnClose
                styles={{ body: { maxHeight: "60vh", overflowY: "auto" } }}
            >
                {loadingModalModels ? (
                    <div className="text-center py-8 text-gray-400">Loading models…</div>
                ) : modalModels.length === 0 ? (
                    <div className="text-gray-400 py-8 text-center">No models found for this vehicle.</div>
                ) : (
                    <div>
                        <div className="mb-2 flex items-center justify-between">
                            <div className="flex items-center gap-1">
                                <Button type="link" size="small" style={{ padding: "0 4px" }} onClick={() => setModalSelectedModels(modalModels.map((m) => m._id))}>
                                    Select All
                                </Button>
                                <span className="text-gray-300">|</span>
                                <Button type="link" size="small" style={{ padding: "0 4px" }} onClick={() => setModalSelectedModels([])}>
                                    Clear All
                                </Button>
                            </div>
                            {modalSelectedModels.length > 0 && (
                                <Tag color="orange" style={{ margin: 0 }}>{modalSelectedModels.length} selected</Tag>
                            )}
                        </div>
                        <div style={{ paddingRight: 4 }}>
                            <Checkbox.Group
                                value={modalSelectedModels}
                                onChange={setModalSelectedModels}
                                style={{ width: "100%", display: "flex", flexDirection: "column" }}
                            >
                                {modalModels.map((model) => (
                                    <div key={model._id} className="flex items-center px-1 py-1 rounded hover:bg-gray-50">
                                        <Checkbox value={model._id} style={{ width: "100%" }}>
                                            {model.en_name}
                                        </Checkbox>
                                    </div>
                                ))}
                            </Checkbox.Group>
                        </div>
                    </div>
                )}
            </Modal>
        </>
    );
};

export default VehicleMappingSection;
